'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
}

interface FAQGlossaryProps {
  terms: GlossaryTerm[];
}

const FAQGlossary = ({ terms }: FAQGlossaryProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  const filteredTerms = terms.filter(term => 
    term.term.charAt(0).toUpperCase() === selectedLetter
  );

  const availableLetters = new Set(
    terms.map(term => term.term.charAt(0).toUpperCase())
  );

  if (!isHydrated) {
    return (
      <div className="glassmorphic rounded-2xl p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
            <Icon name="BookOpenIcon" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold text-foreground">AI Woordenlijst</h2>
            <p className="text-sm text-muted-foreground">Begrippen uitgelegd voor zakelijk gebruik</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {alphabet.map(letter => (
            <div
              key={letter}
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-muted-foreground text-sm font-medium"
            >
              {letter}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glassmorphic rounded-2xl p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
          <Icon name="BookOpenIcon" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-display font-bold text-foreground">AI Woordenlijst</h2>
          <p className="text-sm text-muted-foreground">Begrippen uitgelegd voor zakelijk gebruik</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {alphabet.map(letter => (
          <button
            key={letter}
            onClick={() => setSelectedLetter(letter)}
            disabled={!availableLetters.has(letter)}
            className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-300 ${
              selectedLetter === letter
                ? 'bg-primary text-primary-foreground glow-primary'
                : availableLetters.has(letter)
                ? 'bg-muted text-foreground hover:bg-primary/20'
                : 'bg-muted/50 text-muted-foreground cursor-not-allowed opacity-50'
            }`}
          >
            {letter}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredTerms.length > 0 ? (
          filteredTerms.map((item, index) => (
            <div key={index} className="bg-muted rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedTerm(expandedTerm === item.term ? null : item.term)}
                className="w-full p-4 text-left flex items-center justify-between hover:bg-primary/10 transition-colors duration-300"
              >
                <div>
                  <h4 className="font-semibold text-foreground">{item.term}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{item.category}</p>
                </div>
                <Icon 
                  name="ChevronDownIcon" 
                  size={20} 
                  className={`text-muted-foreground transition-transform duration-300 ${
                    expandedTerm === item.term ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedTerm === item.term && (
                <div className="px-4 pb-4 border-t border-border">
                  <p className="text-sm text-muted-foreground leading-relaxed pt-4">
                    {item.definition}
                  </p>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Geen termen gevonden voor letter {selectedLetter}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQGlossary;